import { useState, useEffect, Fragment } from 'react'
import { Deal } from '../../models/shared/Deal'
import { Offer, OfferBlockType, OfferLineItem } from '../../models/shared/Offer';
import { v4 as uuidv4 } from 'uuid';
import NewBlock from './NewBlock';
import { useFirestore } from 'reactfire';
import { doc, updateDoc } from 'firebase/firestore';
import { COLLECTIONS, DND_ACTIVATION_DELAY_CONSTRAINT, DND_ACTIVATION_DISTANCE_CONSTRAINT } from '../../utils/shared/constants';
import { useOrganization } from '../../context/OrganizationContext';
import { toast } from 'react-toastify';
import { useText } from '../../context/TextContext';
import { useSaveToast } from '../../context/SaveToastContext';
import Block from './Block';
import {
    DndContext,
    DragOverEvent,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

interface Props {
    deal: Deal;
    offer: Offer;
}

let timeout: NodeJS.Timeout | null = null;

export default function OfferEditor(props: Props) {
    const { offer: originalOffer, deal } = props;
    const [offer, setOffer] = useState<Offer>(originalOffer);
    const [activeBlockIx, setActiveBlockIx] = useState<number | null>(null);
    const [overBlockIx, setOverBlockIx] = useState<number | null>(null);
    const [newlyCreatedBlockIx, setNewlyCreatedBlockIx] = useState<number | null>(null);
    const firestore = useFirestore();
    const { orgId } = useOrganization();
    const text = useText();
    const { markSaved, startSaving, stopSaving } = useSaveToast();

    // Setup DnD sensors for drag and drop
    const sensors = useSensors(
        useSensor(TouchSensor, { activationConstraint: DND_ACTIVATION_DELAY_CONSTRAINT }),
        useSensor(MouseSensor, { activationConstraint: DND_ACTIVATION_DISTANCE_CONSTRAINT }),
    );

    const handleDragOver = (event: DragOverEvent) => {
        const { active, over } = event;

        if (!over) {
            return;
        }

        const activeIx = Number(active.id);
        const overIx = Number(over.id);

        setActiveBlockIx(activeIx);
        setOverBlockIx(overIx);
    };

    const handleDragEnd = () => {
        if (overBlockIx === null || activeBlockIx === null) return;

        if (activeBlockIx !== overBlockIx) {
            moveBlock(activeBlockIx, overBlockIx);
        }
        setActiveBlockIx(null);
        setOverBlockIx(null);
    };

    const moveBlock = (fromIndex: number, toIndex: number) => {
        const updatedBlocks = [...offer.blocks];
        const [movedBlock] = updatedBlocks.splice(fromIndex, 1);
        updatedBlocks.splice(toIndex, 0, movedBlock);

        handleOfferUpdate({ blocks: updatedBlocks });
    };

    const handleAddBlock = async (type: OfferBlockType, ix: number) => {
        const newBlocks = [...offer.blocks || []];

        // Initialize the block with type-specific properties
        let newBlock: any = { type };

        // Add default properties based on block type
        if (type === 'spacer') {
            newBlock.size = 'medium';
        } else if (type === 'lineItems') {
            newBlock.fontSize = 16;
            newBlock.showBorders = false;

            // Create one empty line item by default
            const emptyLineItem: OfferLineItem = {
                id: uuidv4(),
                description: '',
                quantity: 1,
                unitPrice: 0,
                vatRate: 0,
            };
            handleOfferUpdate({
                lineItems: [...(offer.lineItems || []), emptyLineItem]
            });
        }

        newBlocks.splice(ix, 0, newBlock);

        setNewlyCreatedBlockIx(ix);
        handleOfferUpdate({ blocks: newBlocks });
    };

    // Update local state when the original offer changes
    useEffect(() => {
        setOffer(originalOffer);
    }, [originalOffer]);

    const handleOfferUpdate = async (update: Partial<Offer>) => {
        // Update local state immediately
        const updatedOffer = {
            ...offer,
            ...update,
            updatedAt: new Date()
        };

        setOffer(updatedOffer);

        // Clear previous timeout if it exists
        if (timeout) {
            clearTimeout(timeout);
        }

        // Set a new timeout to save changes after a delay
        timeout = setTimeout(async () => {
            try {
                const saveId = startSaving();

                const offerRef = doc(
                    firestore,
                    `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${deal.id}/${COLLECTIONS.OFFERS}/${offer.id}`
                );

                await updateDoc(offerRef, {
                    ...update,
                    updatedAt: new Date()
                });

                markSaved(saveId);
            } catch (error) {
                console.error('Error updating offer', error);
                stopSaving();
                toast.error(text.defaultError);
            }
        }, 1000);
    }

    return (
        <div className="d-flex flex-column shadow p-5 rounded bg-offer">
            <NewBlock onAddBlock={handleAddBlock} ix={0} />

            <DndContext
                sensors={sensors}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={offer.blocks?.map((_, ix) => ix) || []}
                    strategy={verticalListSortingStrategy}
                >
                    {offer.blocks?.map((_, ix) => {
                        let className = '';
                        if (activeBlockIx != null && overBlockIx === ix && activeBlockIx !== overBlockIx) {
                            className = activeBlockIx > ix ? 'drop-indicator-top' : 'drop-indicator-bottom';
                        }

                        return (<Fragment key={ix}>
                            <Block
                                ix={ix}
                                offer={offer}
                                deal={deal}
                                onOfferUpdate={handleOfferUpdate}
                                onMoveBlock={moveBlock}
                                className={className}
                                autoFocus={ix === newlyCreatedBlockIx}
                            />

                            <NewBlock
                                onAddBlock={handleAddBlock}
                                ix={ix + 1}
                            />
                        </Fragment>)
                    })}
                </SortableContext>
            </DndContext>
        </div>
    )
}
